import React, { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import MilestoneForm from "@pages/Program/components/milestones/MilestoneForm";
import { MilestonePayload } from "@shared/interfaces/ProgramFiles";
import useHandleSaveMilestone from "@pages/Program/components/milestones/utils/useHandleSaveMilestone";
import { useMilestone } from "@pages/Program/components/milestones/utils/useMilestone";
import MilestoneFormSkeleton from "@pages/Program/components/milestones/skeleton/MilestoneFormSkeleton";

interface EditMilestoneProps {
  isOpen: boolean;
  onClose: () => void;
  milestoneId: string;
  fromLS?: boolean;
  fetchMilestone: () => void;
  startDate: string;
  endDate: string;
  onDelete: () => void;
}

const EditMilestone: React.FC<EditMilestoneProps> = ({
  isOpen,
  onClose,
  milestoneId,
  fetchMilestone,
  startDate,
  endDate,
  onDelete,
}) => {
  const [loading, setLoading] = useState(true);
  const [dataFetching, setDataFetching] = useState(true);
  const methods = useForm<MilestonePayload>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "all",
    defaultValues: {
      name: "",
      description: "",
      start_date: "",
      end_date: "",
      project_id: 0,
      template_id: 0,
      responsible_user_id: 0,
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isValid },
  } = methods;

  const { getMilestoneById } = useMilestone();
  const handleSaveMilestone = useHandleSaveMilestone({ milestoneId }, methods);

  useEffect(() => {
    const loadMilestone = async () => {
      setLoading(true);
      setDataFetching(true);
      try {
        const milestone = await getMilestoneById(milestoneId);
        if (milestone) {
          reset({
            ...milestone,
            project_id: milestone.project_id,
            template_id: milestone.template_id,
            goal_id: milestone.goal_id,
          });

          setTimeout(() => {
            setDataFetching(false);
          }, 2000);
        }
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && milestoneId) {
      loadMilestone();
    }
  }, [isOpen, milestoneId, reset]);

  const onSubmit = async (values: MilestonePayload) => {
    if (!isValid) return;

    try {
      setLoading(true);
      const payload: MilestonePayload = {
        ...values,
        reporting_frequency: null,
      };

      await handleSaveMilestone(payload);
      fetchMilestone();
      onClose();
      reset({
        name: "",
        description: "",
        start_date: "",
        end_date: "",
        project_id: undefined,
        template_id: undefined,
        goal_id: undefined,
        responsible_user_id: undefined,
      });
    } catch (error) {
      console.error("Error saving milestone:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-30 flex items-center justify-center">
      <FormProvider {...methods}>
        <div className="bg-white rounded-lg w-full max-w-2xl py-6 shadow-lg h-[95vh] flex flex-col">
          <h2 className="text-xl font-semibold pb-4 mb-0 border-b border-[#e5e7eb] px-6">
            {loading ? "Loading..." : "Edit Milestone"}
          </h2>

          {loading ? (
            <MilestoneFormSkeleton />
          ) : (
            <>
              <MilestoneForm
                dataLoading={dataFetching}
                startDate={startDate}
                endDate={endDate}
                onDelete={onDelete}
              />
              <div className="flex justify-end mt-6 gap-3 px-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-100 text-sm 2xl:text-base"
                >
                  Close
                </button>
                <button
                  type="submit"
                  disabled={!isValid}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm 2xl:text-base"
                  onClick={handleSubmit(onSubmit)}
                >
                  Save Changes
                </button>
              </div>
            </>
          )}
        </div>
      </FormProvider>
    </div>
  );
};

export default EditMilestone;
